import * as React from 'react';
import EkonomiskAnalysSelectView from './Components/EkonomiskAnalysSelectView/EkonomiskAnalysSelectView';
import {
  ISelectedAnalys,
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../../EkonomiskAnalysPage';
import { EkonomiskAnalysSelectConsumer } from '../Components/AnalysSelect/Context/EkonomiskAnalysSelectContext';
import { clone } from 'ramda';
import EDKeybind from '../../../../../../../../BaseComponents/Keybinds/EDKeybind';
import { KodplanConsumer } from '../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import { analyserToSections } from './Components/FavoriteAnalysDialog/Utils/EkonomiskAnalysFavoriteAnalysDialogUtils';
import { IAnalysGroupingModeUserInfo } from '../../../Utils/ekonomiskAnalysInitQueryUtils';
import {
  ISection,
  ISuggestion,
} from '../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import {
  suggestionToAnalys,
  suggestionToCodePrivilege,
} from '../../../Utils/convertUtils';
import {
  recentAnalyserIdName,
  attKvitteraIdName,
} from './Components/EkonomiskAnalysSelectList/EkonomiskAnalysSelectList';
import AnalyserQuery from '../../../Queries/EkonomiskAnalysListQuery/EkonomiskAnalysListQuery';
import { useIsTouchDevice } from '../../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { useLocation, useNavigate } from 'react-router';

interface IProps {
  selectingAnalys?: boolean;
  database: string;
  selectedAnalys: ISelectedAnalys | null;
  onSelected: (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    sammanfattningsMode: boolean
  ) => void;
  startAnalysId?: string | null;
  sideMenuIsOpen: boolean;
  openSideMenu: () => void;
  analysSelectOpen: boolean;
  setAnalysSelectOpen: (newValue: boolean) => void;
  analysGroupingMode?: IAnalysGroupingModeUserInfo;
  disabled?: boolean;
  disabledReason?: string;
  sammanfattningsMode: boolean;
}

interface IRouterProps {
  clearSearchParams: () => void;
}

interface IInnerProps extends IProps, IRouterProps {
  sections: ISection[];
  selectInputRef: React.RefObject<HTMLInputElement | null>;
}

interface IWrappedProps extends IInnerProps {
  isTouchDevice: boolean;
}

interface IState {
  searchValue: string;
  displayValue: string | null;
  openCodePrivileges: string[];
  tabIndex: number;
  fullscreen: boolean;
  hideInlineInput: boolean;
}

/**
 * Yttre
 */
class EkonomiskAnalysSelectBase extends React.Component<IWrappedProps, IState> {
  constructor(props: IWrappedProps) {
    super(props);

    this.state = {
      searchValue: '',
      displayValue: props.selectedAnalys
        ? props.selectedAnalys.analys.name
        : null,
      openCodePrivileges: [],
      tabIndex: 1,
      fullscreen: false,
      hideInlineInput: false,
    };
  }

  componentDidUpdate(prevProps: IInnerProps) {
    // Don't remember state from opening/closing the thing
    if (prevProps.sideMenuIsOpen && !this.props.sideMenuIsOpen) {
      this.setState({
        searchValue: '',
        displayValue: this.props.selectedAnalys
          ? this.props.selectedAnalys.analys.name
          : null,
        openCodePrivileges: [],
        tabIndex: 1,
      });
    }

    const isFirstAnalys =
      !prevProps.selectedAnalys && this.props.selectedAnalys;
    const analysChanged =
      prevProps.selectedAnalys &&
      this.props.selectedAnalys &&
      prevProps.selectedAnalys.analys.name !==
        this.props.selectedAnalys.analys.name;

    const analysDeselected =
      prevProps.selectedAnalys && !this.props.selectedAnalys;

    if (isFirstAnalys || analysChanged || analysDeselected) {
      if (this.props.analysSelectOpen && !analysDeselected) {
        this.toggleSelectingAnalys();
      } else {
        this.setState(
          {
            searchValue: '',
            displayValue: this.props.selectedAnalys
              ? this.props.selectedAnalys.analys.name
              : null,
          },
          () => {
            if (analysDeselected) {
              this.focusTextInput();
            }
          }
        );
      }
    }
  }

  openFullscreen = (callback?: () => void) => {
    this.setState(
      prevState => ({
        fullscreen: true,
        hideInlineInput: false,
      }),
      callback
    );
  };

  closeFullscreen = (callback?: () => void) => {
    this.setState(
      {
        fullscreen: false,
        hideInlineInput: false,
      },
      callback
    );
  };

  onEnterFullscreen = () => {
    this.setState({
      hideInlineInput: true,
    });
  };

  onSuggestionSelected = (
    suggestion: ISuggestion,
    clearSearch: () => void,
    section: ISuggestion,
    sammanfattningsMode: boolean
  ): void => {
    // Rensa sök-parametrar i URL:en när en analys väljs, så att parametrar från en detaljanalys inte hänger kvar
    this.props.clearSearchParams();

    this.props.setAnalysSelectOpen(false);
    this.setState(
      prevState => ({
        searchValue: '',
        displayValue: suggestion.name,
        openCodePrivileges: [],
      }),
      () => {
        this.props.onSelected(
          suggestionToAnalys(suggestion),
          suggestionToCodePrivilege(section),
          sammanfattningsMode
        );
      }
    );
  };

  setOpenCodePrivilege = (codePrivilege: string | null) => () => {
    if (codePrivilege == null) {
      return;
    }
    const isAlreadyActive =
      this.state.openCodePrivileges.includes(codePrivilege);
    this.setState(prevState => ({
      openCodePrivileges: isAlreadyActive
        ? prevState.openCodePrivileges.filter(code => code !== codePrivilege)
        : [...clone(prevState.openCodePrivileges), codePrivilege],
    }));
  };

  focusTextInput = () => {
    if (this.props.selectInputRef.current) {
      this.props.selectInputRef.current.focus({ preventScroll: true });
    }
  };

  clearAndFocusSearchInput = () => {
    this.setState(
      {
        searchValue: '',
      },
      this.props.isTouchDevice ? undefined : this.focusTextInput
    );
  };

  openCodePrivilege = (selectedAnalys: ISelectedAnalys) => {
    this.setState({
      openCodePrivileges: [selectedAnalys.codePrivilege.id],
    });
  };

  toggleSelectingAnalys = () => {
    if (!this.props.sideMenuIsOpen) {
      this.props.openSideMenu();
    }
    this.props.setAnalysSelectOpen(!this.props.analysSelectOpen);

    const opening = !this.props.analysSelectOpen;
    if (opening) {
      this.clearAndFocusSearchInput();
    } else {
      // Closing
      this.setInputValues();
    }
  };

  setInputValues = () => {
    this.setState({
      openCodePrivileges: [],
      searchValue: this.props.selectedAnalys
        ? this.props.selectedAnalys.analys.name
        : '',
      displayValue: this.props.selectedAnalys
        ? this.props.selectedAnalys.analys.name
        : null,
    });
  };

  onSearchValueChanged = (newValue: string) => {
    this.setState({
      searchValue: newValue,
      tabIndex: 1,
      openCodePrivileges:
        newValue === ''
          ? []
          : [
              recentAnalyserIdName,
              attKvitteraIdName,
              ...this.props.sections.map(section => section.value),
            ],
    });
  };

  filterSearch = () => {
    if (this.state.searchValue.length === 0 || !this.props.analysSelectOpen) {
      return this.props.sections;
    }

    const searchValue = this.state.searchValue;

    return this.props.sections.filter(section =>
      section.items.some(analys =>
        analys.name
          .toLowerCase()
          .trim()
          .includes(searchValue.toLowerCase().trim())
      )
    );
  };

  /**
   * Används för att välja bland utdelade analyser med piltangenterna
   * Alla ListItems + sjävla sök inputen får manuellt klassen arrowMoveTarget
   */
  browseSuggestionsUp = () => {
    const allSuggestionNodes = document.querySelectorAll('.arrowMoveTarget');

    this.setState(
      prevState => ({
        tabIndex:
          prevState.tabIndex - 1 < 1
            ? allSuggestionNodes.length - 1
            : prevState.tabIndex - 1,
      }),
      () => {
        if (
          allSuggestionNodes.length > 0 &&
          this.state.tabIndex < allSuggestionNodes.length &&
          this.state.tabIndex >= 1
        ) {
          (
            allSuggestionNodes.item(this.state.tabIndex) as HTMLDivElement
          ).focus();
        }
      }
    );
  };

  browseSuggestionsDown = () => {
    const allSuggestionNodes = document.querySelectorAll('.arrowMoveTarget');

    this.setState(
      prevState => ({
        tabIndex:
          prevState.tabIndex + 1 >= allSuggestionNodes.length
            ? 1
            : prevState.tabIndex + 1,
      }),
      () => {
        if (
          allSuggestionNodes.length > 0 &&
          this.state.tabIndex < allSuggestionNodes.length &&
          this.state.tabIndex >= 1
        ) {
          (
            allSuggestionNodes.item(this.state.tabIndex) as HTMLDivElement
          ).focus();
        }
      }
    );
  };

  onSearchInputClicked = () => {
    this.setState(prevState => ({
      tabIndex: 1,
      displayValue: null,
      searchValue: this.props.isTouchDevice ? '' : prevState.searchValue,
    }));
  };

  setDisplayValue = (newValue: string) => {
    this.setState({ displayValue: newValue });
  };

  render() {
    return (
      <EDKeybind keymap="ArrowUp" onKeyDown={this.browseSuggestionsUp}>
        <EDKeybind keymap="ArrowDown" onKeyDown={this.browseSuggestionsDown}>
          <EkonomiskAnalysSelectView
            openCodePrivileges={this.state.openCodePrivileges}
            setOpenCodePrivilege={this.setOpenCodePrivilege}
            searchValue={this.state.searchValue}
            onSearchValueChanged={this.onSearchValueChanged}
            selectListOpen={this.props.analysSelectOpen}
            toggleSelectListOpen={this.toggleSelectingAnalys}
            selectedAnalys={this.props.selectedAnalys}
            onAnalysSelected={this.onSuggestionSelected}
            sections={this.filterSearch()}
            sideMenuIsOpen={this.props.sideMenuIsOpen}
            onSearchInputClicked={this.onSearchInputClicked}
            displayValue={this.state.displayValue}
            setDisplayValue={this.setDisplayValue}
            fullscreen={this.state.fullscreen}
            hideInlineInput={this.state.hideInlineInput}
            openFullscreen={this.openFullscreen}
            onEnterFullscreen={this.onEnterFullscreen}
            closeFullscreen={this.closeFullscreen}
            setInputValues={this.setInputValues}
            disabled={this.props.disabled}
            disabledReason={this.props.disabledReason}
            sammanfattningsMode={this.props.sammanfattningsMode}
          />
        </EDKeybind>
      </EDKeybind>
    );
  }
}

export const EkonomiskAnalysSelect: React.FC<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const isTouchDevice = useIsTouchDevice();
  return <EkonomiskAnalysSelectBase {...props} isTouchDevice={isTouchDevice} />;
};

/**
 * Inre
 */
const EkonomiskAnalysSelectQuery = (props: IProps & IRouterProps) => {
  return (
    <KodplanConsumer>
      {({ activeKodplan }) => (
        <AnalyserQuery
          onlyFavorites
          codeSystemId={activeKodplan ? activeKodplan.id : ''}
        >
          {selections => {
            let sections: ISection[] = analyserToSections(
              selections,
              props.analysGroupingMode
                ? props.analysGroupingMode.value
                : 'kodbeh'
            );

            return (
              <EkonomiskAnalysSelectConsumer>
                {({ selectInputRef }) => (
                  <EkonomiskAnalysSelect
                    {...props}
                    sections={sections}
                    selectInputRef={selectInputRef}
                  />
                )}
              </EkonomiskAnalysSelectConsumer>
            );
          }}
        </AnalyserQuery>
      )}
    </KodplanConsumer>
  );
};

const EkonomiskAnalysSelectWithRouter = (props: IProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const clearSearchParams = React.useCallback(() => {
    if (!location.search) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  return (
    <EkonomiskAnalysSelectQuery
      {...props}
      clearSearchParams={clearSearchParams}
    />
  );
};

export default EkonomiskAnalysSelectWithRouter;
